import { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Response } from './Response';

import { UserResponse, Value, ViewableResponse } from '../types/global';
import { recordResponses } from '../reducers/graph';
import { partitionAutocompleteResponses } from '../util/autocompleteResponses';
import { RootState, UserResponseStore } from '../types/graph';
import { Question } from './Question';
import { MoreButton } from './MoreButton';
import { useAppContext } from '../context/appContext';
import { ResponseAutocomplete } from './ResponseAutocomplete';
import { isEqual } from 'lodash';
import { isStructuredValue } from '@aldebaran/common/util/structuredValue';

interface KnowledgebaseQuestionProps {
  extId: string;
  currentPage: string;
  error?: boolean;
  resetErrors: () => void;
  onFreshSelection?: () => void;
  style?: React.CSSProperties;
}

const expandedLimit = 5;
const textOrNumberFormats = [
  'TEXT',
  'MULTILINE_TEXT',
  'Field',
  'multiLineText',
  'INTEGER',
  'NUMBER',
  'DECIMAL',
];

export const KnowledgebaseQuestion = ({
  extId,
  currentPage,
  error,
  resetErrors,
  onFreshSelection,
  style,
}: KnowledgebaseQuestionProps) => {
  const dispatch = useDispatch();
  const { debugMode, setDebugQuestion } = useAppContext();

  const question = useSelector((state: RootState) => state.kb.questions[extId]);
  const questionnnaire = useSelector((state: RootState) => state.questionnaire);

  const userResponses = useSelector(
    (state: RootState) => state.userResponses[extId] ?? {},
  );
  const sectionAssignments = useSelector(
    (state: RootState) => state.questionnaire.sectionAssignments,
  );

  const [values, setValues] = useState<UserResponseStore>(userResponses);
  const [expanded, setExpanded] = useState(false);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (!isEqual(values, userResponses)) {
      setValues(userResponses);
    }
  }, [userResponses]);

  const { autocompleteResponses, belowResponses } = useMemo(
    () => partitionAutocompleteResponses(question?.responses ?? []),
    [question?.responses],
  );

  if (!question) return null;
  const { responses } = question;

  const exclusiveResponses =
    responses.filter((r) => r.exclusive).map((r) => r.extId) ?? [];

  const demo = false;

  const createChangeHandler =
    (response: ViewableResponse) => (value: Value) => {
      resetErrors();

      const currentSection = question.section || sectionAssignments[extId];
      const toRecordedResponses = (
        nextValues: UserResponseStore,
      ): UserResponse[] =>
        question.responses
          .filter((r) => r.extId in nextValues)
          .map((r) => ({
            ...r,
            value: { value: nextValues[r.extId], unit: r.unit },
          }));

      const dispatchResponses = (updatedResponses: UserResponse[]) => {
        dispatch(
          recordResponses({
            questionId: extId,
            currentPage,
            currentSection,
            responses: updatedResponses,
          }),
        );
      };

      const isTextOrNumberField = textOrNumberFormats.includes(
        response.format || '',
      );
      const isAutocompleteOtherSelection =
        response.format === 'AUTOCOMPLETE_OTHER' &&
        typeof value === 'string' &&
        value.trim() !== '';
      const behavesLikeSelection =
        value === true ||
        isAutocompleteOtherSelection ||
        isStructuredValue(value);
      const shouldClearAll =
        !isTextOrNumberField &&
        (question.multipleChoice === false || response.exclusive);
      const shouldRecordWithSiblingClears =
        shouldClearAll || behavesLikeSelection;

      if (shouldRecordWithSiblingClears) {
        const nextValues = { ...values };
        if (shouldClearAll) {
          Object.keys(values).forEach((key) => {
            if (key !== response.extId) nextValues[key] = false;
          });
        } else {
          exclusiveResponses.forEach((key) => {
            if (key !== response.extId) nextValues[key] = false;
          });
        }
        nextValues[response.extId] = value;

        dispatchResponses(toRecordedResponses(nextValues));

        if (value === true) {
          // Meant for auto-navigation after response selection
          onFreshSelection?.();
        }
      } else {
        dispatchResponses([
          { ...response, value: { value, unit: response.unit } },
        ]);

        // Trigger fresh selection only for OTHER_TEXT (ResponseOtherField modal)
        // Don't trigger for TEXT/MULTILINE_TEXT/NUMBER/etc. where user types continuously
        if (response.format === 'OTHER_TEXT') {
          const hasValue =
            value !== undefined &&
            value !== null &&
            (typeof value !== 'string' || value.trim() !== '');
          if (hasValue) {
            onFreshSelection?.();
          }
        }
      }
    };

  const expandable =
    question.paginateResponses &&
    !expanded &&
    question.responses.length - expandedLimit > 2;

  return (
    <div
      className={`transition-opacity duration-700 ease-in-out ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      style={style}
    >
      {question.label ? (
        <>
          <Question
            id={'question_' + question.extId}
            onClickLabel={() => setDebugQuestion(question.extId)}
            error={error}
            required={question.required}
            label={
              debugMode
                ? `(${question.extId}) ${question.label}`
                : question.label
            }
            comment={
              debugMode
                ? `(After: ${question.after}) [Assigned section: ${
                    question.section
                  }] [Inferred section: ${sectionAssignments[question.extId]}]
                  [Inserted by: ${questionnnaire.insertedBy[question.extId]}]
                  ${question.comment ? ' - ' + question.comment : ''}`
                : question.comment
            }
            format={question.format}
          >
            {question.format === 'AUTOCOMPLETE' ? (
              <>
                <ResponseAutocomplete
                  multipleChoice={question.multipleChoice}
                  questionLabel={question.label ?? ''}
                  questionDescription={question.comment}
                  placeholder={question.placeholder}
                  questionExtId={question.extId}
                  responses={autocompleteResponses}
                  hideResponsesByDefault={question.hideResponsesByDefault}
                  onChange={(response, value) => {
                    const handler = createChangeHandler(response);
                    handler(value);
                  }}
                />
                {belowResponses.map((response) => (
                  <Response
                    key={response.extId}
                    response={response}
                    onChange={createChangeHandler(response)}
                    value={values[response.extId]}
                    questionLabel={question.label}
                    questionComment={question.comment}
                    questionAlwaysShowLabel={question.alwaysShowLabel}
                    multiselect={question.multipleChoice}
                    disabled={demo}
                  />
                ))}
              </>
            ) : (
              <>
                {(expandable
                  ? responses.slice(0, expandedLimit)
                  : responses
                ).map((response) => (
                  <Response
                    key={response.extId}
                    response={response}
                    onChange={createChangeHandler(response)}
                    value={values[response.extId]}
                    questionLabel={question.label}
                    questionComment={question.comment}
                    questionAlwaysShowLabel={question.alwaysShowLabel}
                    multiselect={question.multipleChoice}
                    disabled={demo}
                  />
                ))}
                {expandable && (
                  <MoreButton
                    onExpand={() => setExpanded(true)}
                    isExpanded={expanded}
                  >
                    Voir plus de réponses
                  </MoreButton>
                )}
                {expanded && (
                  <MoreButton
                    onExpand={() => setExpanded(false)}
                    isExpanded={expanded}
                  >
                    Voir moins de réponses
                  </MoreButton>
                )}
              </>
            )}
          </Question>
        </>
      ) : null}
    </div>
  );
};
